import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { GraphData } from 'force-graph';
import { GalaxyNode, GalaxyNodeType } from '../models/galaxy-node';
import { GalaxyLink } from '../models/galaxy-link';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class GalaxyDataService {
  constructor(private http: HttpClient) {}

  fetchData(
    url: string,
    typeMap: Map<string, GalaxyNodeType>,
    ringMap: Map<string, number>,
  ): Observable<GraphData<GalaxyNode, GalaxyLink>> {
    return new Observable((observer) => {
      fetch(url)
        .then((response) => response.json())
        .then((data) => {
          const processedData = this.processAsGalaxyData(data, typeMap, ringMap);
          observer.next(processedData);
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }

  processAsGalaxyData(
    graphData: GraphData<any>,
    typeMap: Map<string, GalaxyNodeType>,
    ringMap: Map<string, number>,
  ): GraphData<GalaxyNode, GalaxyLink> {
    const nodes: GalaxyNode[] = [];
    let links: GalaxyLink[] = [];

    const oldNodeMap: Map<string, any> = new Map();
    for (const node of graphData.nodes) {
      oldNodeMap.set(node.id, node);
    }

    const newNodeMap: Map<string, GalaxyNode> = new Map();
    for (const link of graphData.links) {
      let sourceNode = oldNodeMap.get(link.source)!;
      let targetNode = oldNodeMap.get(link.target)!;
      if (!sourceNode || !targetNode) continue;
      if (!typeMap.has(sourceNode.type) || !typeMap.has(targetNode.type)) continue;
      const oldSourceNodeId = sourceNode.id;
      const oldTargetNodeId = targetNode.id;
      const sourceNodeType = typeMap.get(sourceNode.type)!;
      const targetNodeType = typeMap.get(targetNode.type)!;
      const sourceNodeRingNumber = ringMap.get(sourceNode.techMaturity)!;
      const targetNodeRingNumber = ringMap.get(targetNode.techMaturity)!;

      if (!GalaxyLink.isValidLink(sourceNodeType, targetNodeType)) continue;

      const sourceData = {
        techMaturity: sourceNode.techMaturity,
        team: sourceNode.team,
        projectStatus: sourceNode.projectStatus,
        path: sourceNode.path,
      };
      const targetData = {
        techMaturity: targetNode.techMaturity,
        team: targetNode.team,
        projectStatus: targetNode.projectStatus,
        path: targetNode.path,
      };

      if (!newNodeMap.has(oldSourceNodeId)) {
        sourceNode = new GalaxyNode(oldSourceNodeId, sourceNodeType, sourceNodeRingNumber, sourceData);
      } else {
        sourceNode = newNodeMap.get(oldSourceNodeId)!;
      }
      if (targetNodeType === GalaxyNodeType.PLANET || !newNodeMap.has(oldTargetNodeId)) {
        targetNode = new GalaxyNode(oldTargetNodeId, targetNodeType, targetNodeRingNumber, targetData);
      } else if (newNodeMap.has(oldTargetNodeId)) {
        targetNode = newNodeMap.get(oldTargetNodeId)!;
      }

      newNodeMap.set(oldSourceNodeId, sourceNode);
      newNodeMap.set(oldTargetNodeId, targetNode);

      if (oldSourceNodeId === oldTargetNodeId) continue; // Prevent self-loops

      if (!nodes.includes(sourceNode)) {
        nodes.push(sourceNode);
      }
      if (!nodes.includes(targetNode)) {
        nodes.push(targetNode);
      }

      const galaxyLink: GalaxyLink = new GalaxyLink(
        sourceNode,
        targetNode,
        GalaxyLink.determineType(sourceNodeType, targetNodeType)!,
      );

      links.push(galaxyLink);
    }

    const uniqueLinks = new Map();
    links = links.filter((link) => {
      const key = `${link.target.id}`;
      if (uniqueLinks.has(key)) {
        return false;
      }
      uniqueLinks.set(key, true);
      return true;
    });

    return { nodes, links };
  }
}
